import { CommonModule, KeyValue } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { PublicPageSettings } from 'src/app/models/preference-settings';
import { AuthStoreAction, AuthStoreSelectors, RootStoreState } from 'src/app/root-store';
import { PreferencesSelectors } from 'src/app/root-store/preferences-store';
import { AuthService } from 'src/app/services/auth/auth.service';
import { AlertComponent } from 'src/app/shared/alert/alert.component';
import { BaseComponent } from 'src/app/shared/base-components/base-component';
import { environment } from 'src/environments/environment';
import { AuthAttempt } from '../auth-attempt';
import { AuthError } from '../auth-result';
import { ForgotPasswordAttempt } from '../forgot-pwd-attempt';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    AlertComponent,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    TranslatePipe,
  ],
})
export class LoginComponent extends BaseComponent implements OnInit, OnDestroy {
  private readonly ssoReturnUrlStorageKey = 'fo.auth.sso.returnUrl';
  publicPageSettings: PublicPageSettings;
  // resend OTP cooldown (seconds)
  resendCooldownSeconds = 60;
  resendCooldownRemaining = 0;
  private resendCooldownInterval: any = null;
  // Alert messages for resend OTP
  showResendOtpSentAlert = false;
  showResendOtpCooldownAlert = false;

  authAttempt: AuthAttempt;
  loginForm: UntypedFormGroup;
  isSubmitted = false;
  isAuthenticated = false;
  authError: AuthError = null;

  isAdminTentantCogs = false;
  isAdminTenantAuth = false;
  isMultiTenant = false;
  isTwoFactorMethod = false;
  isOneTimePinTwoFactorMethod = false;
  tenants: KeyValue<string, string>[];
  loginMultitenantForm: UntypedFormGroup;
  loginOneTimePinForm: UntypedFormGroup;
  headerImage: string;
  logintryId: number;

  isForgotForm = false;
  forgotAttempt: ForgotPasswordAttempt;
  forgotPasswordForm: UntypedFormGroup;
  isForgotSubmitted = false;
  isForgotMailSent = false;
  forgotError: AuthError = null;
  returnUrl: string;
  isLoading = false;

  constructor(
    private sanitizer: DomSanitizer,
    private authService: AuthService,
    private store: Store<RootStoreState.State>,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
  ) {
    super();
  }

  ngOnInit() {
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
    this.isMultiTenant = false;
    this.isTwoFactorMethod = false;
    this.isOneTimePinTwoFactorMethod = false;
    this.isForgotForm = false;
    this.loginForm = this.formBuilder.group({
      email: ['', Validators.required],
      password: ['', Validators.required],
    });
    this.loginMultitenantForm = this.formBuilder.group({
      tenant: ['', Validators.required],
    });
    this.loginOneTimePinForm = this.formBuilder.group({
      otp: ['', Validators.required],
    });
    this.forgotPasswordForm = this.formBuilder.group({
      email: ['', Validators.required],
    });
    this.subscribe(
      combineLatest([
        this.store.pipe(select(AuthStoreSelectors.selectIsAuthenticated)),
        this.store.pipe(select(AuthStoreSelectors.selectIsAuthenticatedTenantAdmin)),
      ]),
      ([isAuth, isAuthTenantAdmin]) => {
        this.isLoading = false;
        this.isAuthenticated = isAuth || isAuthTenantAdmin;
        // if (this.isAuthenticated) {
        //   setTimeout(() => {
        //     return this.dialogRef.close();
        //   }, 3000);
        // }
      },
    );
    this.subscribe(this.store.pipe(select(AuthStoreSelectors.selectTenantList)), (tenants) => {
      this.isLoading = false;
      this.isMultiTenant = this.isSubmitted && tenants && tenants.length > 1;
      if (this.isMultiTenant) {
        this.tenants = tenants;
        this.loginMultitenantForm.setValue({ tenant: this.tenants[0].key });
      }
    });
    this.subscribe(this.store.pipe(select(AuthStoreSelectors.selectTwoFactorMethod)), (twoFactorMethod) => {
      this.isLoading = false;
      this.isTwoFactorMethod = twoFactorMethod != null;
      if (this.isTwoFactorMethod) {
        this.isOneTimePinTwoFactorMethod = twoFactorMethod === 'one-time-pin';
        this.showResendOtpSentAlert = true;
        this.startResendCooldown();
      }
    });
    this.subscribe(this.store.pipe(select(AuthStoreSelectors.selectLoginTryId)), (logintryId) => {
      this.logintryId = logintryId;
    });
    this.subscribe(this.store.pipe(select(AuthStoreSelectors.selectPwdForgotten)), (isForgotten) => {
      this.isForgotMailSent = isForgotten;
      this.isLoading = false;
      // if (this.isForgotMailSent) {
      //   setTimeout(() => {
      //     this.store.dispatch(AuthStoreAction.forgotPwdSuccessDone());
      //     return this.dialogRef.close();
      //   }, 3000);
      // }
    });
    this.subscribe(this.store.pipe(select(AuthStoreSelectors.selectAuthError)), (error) => {
      this.authError = error;
      this.isLoading = false;
    });
    this.subscribe(this.store.pipe(select(AuthStoreSelectors.selectForgotPwdError)), (error) => {
      this.forgotError = error;
      this.isLoading = false;
    });
    this.subscribe(this.authService.getAdminTentantCogsFlag(), ({ data }) => (this.isAdminTentantCogs = data));
    this.subscribe(
      this.store.pipe(select(PreferencesSelectors.selectPreferencesPublicPageSettings)),
      (publicSettings) => (this.publicPageSettings = publicSettings),
    );
    this.subscribe(
      this.store.pipe(select(PreferencesSelectors.selectPreferencesLoginImage)),
      (headerImage) => (this.headerImage = headerImage),
    );
  }

  // Call this method in the image source, it will sanitize it.
  transform(base64Image: string) {
    return this.sanitizer.bypassSecurityTrustResourceUrl(base64Image);
  }

  login(): void {
    this.isLoading = true;
    this.authError = null;
    this.isSubmitted = true;
    if (this.loginForm.invalid) {
      this.isLoading = false;
      return;
    }
    this.authAttempt = new AuthAttempt(
      this.loginForm.value.email,
      this.loginForm.value.password,
      null,
      null,
      this.logintryId,
      this.returnUrl,
    );
    if (this.isAdminTenantAuth) {
      this.store.dispatch(AuthStoreAction.authTenantAdmin({ auth: this.authAttempt }));
    } else {
      this.store.dispatch(AuthStoreAction.authenticate({ auth: this.authAttempt }));
    }
  }

  selectTenant(): void {
    this.isLoading = true;
    this.authError = null;
    this.isSubmitted = true;
    if (this.loginMultitenantForm.invalid) {
      this.isLoading = false;
      return;
    }
    this.authAttempt = new AuthAttempt(
      this.loginForm.value.email,
      this.loginForm.value.password,
      this.loginMultitenantForm.value.tenant,
      this.loginOneTimePinForm.value.otp !== '' ? this.loginOneTimePinForm.value.otp : null,
      this.logintryId,
      this.returnUrl,
    );
    if (!this.isAdminTenantAuth) {
      this.store.dispatch(AuthStoreAction.authenticate({ auth: this.authAttempt }));
    }
  }

  sendOTP(): void {
    this.isLoading = true;
    this.authError = null;
    this.isSubmitted = true;
    this.showResendOtpSentAlert = false;
    this.showResendOtpCooldownAlert = false;
    if (this.loginOneTimePinForm.invalid) {
      this.isLoading = false;
      return;
    }
    this.authAttempt = new AuthAttempt(
      this.loginForm.value.email,
      this.loginForm.value.password,
      this.loginMultitenantForm.value.tenant !== '' ? this.loginMultitenantForm.value.tenant : null,
      this.loginOneTimePinForm.value.otp,
      this.logintryId,
      this.returnUrl,
    );
    if (!this.isAdminTenantAuth) {
      this.store.dispatch(AuthStoreAction.authenticate({ auth: this.authAttempt }));
    }
  }

  resendOTP(): void {
    // If cooldown active, inform user and do not send request
    if (this.resendCooldownRemaining > 0) {
      // show remaining seconds (replace with in-app notifier if available)
      this.showResendOtpCooldownAlert = true;
      return;
    }

    this.authError = null;
    this.isSubmitted = true;
    this.authAttempt = new AuthAttempt(
      this.loginForm.value.email,
      this.loginForm.value.password,
      this.loginMultitenantForm.value.tenant !== '' ? this.loginMultitenantForm.value.tenant : null,
      null,
      this.logintryId,
      this.returnUrl,
    );
    if (!this.isAdminTenantAuth) {
      this.store.dispatch(AuthStoreAction.authenticate({ auth: this.authAttempt }));
      this.showResendOtpSentAlert = true;
      setTimeout(() => {
        this.showResendOtpSentAlert = false;
      }, 5000);
      this.startResendCooldown();
    }
  }

  private startResendCooldown(): void {
    this.clearResendCooldown();
    this.resendCooldownRemaining = this.resendCooldownSeconds;
    this.resendCooldownInterval = setInterval(() => {
      this.resendCooldownRemaining -= 1;
      if (this.resendCooldownRemaining <= 0) {
        this.clearResendCooldown();
      }
    }, 1000);
  }

  private clearResendCooldown(): void {
    if (this.resendCooldownInterval != null) {
      clearInterval(this.resendCooldownInterval);
      this.resendCooldownInterval = null;
    }
    this.resendCooldownRemaining = 0;
    this.showResendOtpCooldownAlert = false;
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.clearResendCooldown();
  }

  forgotPassword(): void {
    this.forgotError = null;
    this.isForgotSubmitted = true;
    if (this.forgotPasswordForm.invalid) {
      this.isLoading = false;
      return;
    }
    this.forgotAttempt = new ForgotPasswordAttempt(this.forgotPasswordForm.value.email);
    this.store.dispatch(AuthStoreAction.forgotPwd({ forgotPwd: this.forgotAttempt }));
  }

  resetLoginFormsStatus() {
    this.loginForm.reset();
    this.loginMultitenantForm.reset();
    this.loginOneTimePinForm.reset();
    this.forgotPasswordForm.reset();
    this.showResendOtpSentAlert = false;
    this.showResendOtpCooldownAlert = false;
    this.isForgotForm = false;
    this.store.dispatch(AuthStoreAction.resetLoginFormsStatus());
  }

  isForgotLoading() {
    return this.isForgotSubmitted && !this.isForgotMailSent && !this.forgotError;
  }

  goTenantAdmin() {
    this.loginForm.reset();
    this.isAdminTenantAuth = true;
  }

  closeTenantAdmin() {
    this.loginForm.reset();
    this.isAdminTenantAuth = false;
  }

  oauthLogin(link: string) {
    this.persistSsoReturnUrl();
    location.href = `${environment.getEndpoint(link)}`;
  }

  private persistSsoReturnUrl(): void {
    if (!this.returnUrl) {
      return;
    }
    try {
      sessionStorage.setItem(this.ssoReturnUrlStorageKey, this.returnUrl);
    } catch {
      // Ignore storage failures and continue SSO flow.
    }
  }

  getOpenAuth() {
    return this.publicPageSettings && this.publicPageSettings.openAuthorizations
      ? this.publicPageSettings.openAuthorizations.filter((oa) => !oa.admin)
      : null;
  }

  getAdminOpenAuth() {
    return this.publicPageSettings && this.publicPageSettings.openAuthorizations
      ? this.publicPageSettings.openAuthorizations.filter((oa) => oa.admin)
      : null;
  }

  get formControls() {
    return this.loginForm.controls;
  }

  get forgotFormControls() {
    return this.forgotPasswordForm.controls;
  }

  get otpFormControls() {
    return this.loginOneTimePinForm.controls;
  }

  get environtment() {
    return environment;
  }
}
